import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { collapseToast, toast, Zoom } from "react-toastify";
import { globalStyles, PAGE_KEYS } from "@client/shared/constants";
import { Dropdown, Tag } from "@client/shared/components";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  debounce,
  exportToFile,
  getFormattedDate,
  getPageConfig,
  setPageConfig,
  queryStringBuilder,
  getUserData,
  updatePaginationOnDelete,
} from "@client/utils";
import NotificationView from "./view";
import style from "./style";
import Service from "./service";
import { AppService } from "@client/shared/services";
import { AppContext } from "@client/shared/contexts";
import { DEFAULT_ERROR_MESSAGE, DELIVERY_TYPE } from "@client/shared/constants";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

let filterHelper = null;

const defaultState = {
  areAllSelected: false,
  cardView: false,
  directionFilters: [],
  dialog: {
    bulkEdit: false,
    delete: false,
    duplicate: false,
    duplicateSuccess: false,
    toggle: {
      all: false,
      rows: false,
    },
  },
  entries: [],
  isDeleting: false,
  isDuplicating: false,
  isFiltering: false,
  isGridView: true,
  isPaginationEnabled: true,
  isLoading: true,
  needsAcknowledgement: null,
  totalEntries: 0,
  filters: [],
  pageSize: 5,
  pageNumber: 1,
  preference: {
    delete: true,
    duplicate: true,
    duplicateSuccess: true,
    bulkEdit: true,
    notificationEdit: true,
    toggle: true,
  },
  rowsBeingDuplicated: [],
  rowsBeingToggled: [],
  search: "",
  sort: null,
  selectedRows: [],
  statusFilters: [],
  toggleAll: false,
  countryList: [],
  order: null,
  orderBy: null,
};

let gridHelpers = null;
const getDisabledRows = (state, rowsToCheck = "selectedRows") => {
  return state.entries.map((entry) => {
    if (state.areAllSelected && rowsToCheck === "selectedRows") {
      return { ...entry, isDisabled: true };
    } else {
      return {
        ...entry,
        isDisabled: !!state[rowsToCheck].find((row) => row.id === entry.id),
      };
    }
  });
};
const onGridReady = (event) => (gridHelpers = event);

const updatePreferences = debounce(
  async ({
    duplicate,
    duplicateSuccess,
    bulkEdit,
    notificationEdit,
    delete: deleteNotification,
    toggle,
  }) => {
    const { error } = await Service.updatePreferences({
      duplicate_notification: duplicate,
      notification_duplicated: duplicateSuccess,
      confirm_bulk_edit: bulkEdit,
      notification_edited: notificationEdit,
      delete_notification: deleteNotification,
      toggle_notification: toggle,
    });
    if (error) {
      toast.error("Failed to update preference.");
    }
  },
  100
);

const Notifications = () => {
  const pageConfig = getPageConfig(PAGE_KEYS.NOTIFICATIONS) || {};
  const { appData } = useContext(AppContext);
  const userData = (getUserData() || {}).user || {};

  const [state, setState] = useState({
    ...defaultState,
    cardView:
			pageConfig && pageConfig.cardView
				? pageConfig.cardView
				: defaultState.cardView,
		pageSize: defaultState.pageSize,
	});

	const navigate = useNavigate();

	// const getQueryString = () => {
	//   const filters = [
	//     ...state.directionFilters,
	//     ...(state.statusFilters.length === 2 ? [] : state.statusFilters),
	//   ];

	//   let searchArray = [];

	//   if (state.search) {
	//     searchArray = ["name", "external_phone_number"].map((element) => {
	//       return { field: element, value: state.search };
	//     });
	//   }

	//   return queryStringBuilder(
	//     state.pageSize,
	//     (state.pageNumber - 1) * state.pageSize,
	//     searchArray,
	//     filters,
	//     state.sort,
	//     state.search !== ""
	//   );
	// };

	const handleDuplicated = (row) => {
		setState((prevState) => {
			const rowsBeingDuplicated = prevState.rowsBeingDuplicated.slice();
			rowsBeingDuplicated.push(row.id);
			return {
				...prevState,
				rowsBeingDuplicated,
			};
		});
	};

	const handleCancelDuplicate = () => {
		setState((prevState) => {
			const rowsBeingDuplicated =
				prevState.rowsBeingDuplicated.length > 0
					? prevState.rowsBeingDuplicated.slice(1)
					: [];
			return {
				...prevState,
				dialog: {
					...prevState.dialog,
					duplicate: false,
				},
				rowsBeingDuplicated,
			};
		});
	};

	const onPreferenceChange = (event, name) => {
		if (!name) return;
		const checked = event.currentTarget.checked;
		setState((prevState) => {
			const updatedPreferences = {
				...prevState.preference,
				[name]: checked
			};
			updatePreferences(updatedPreferences);
			return {
				...prevState,
				preference: updatedPreferences,
			};
		});
	};
	const fetchCountryList = async () => {
		setState((prevState) => ({
			...prevState,
			isLoading: true,
		}));

		const { data, error } = await Service.getCountryList();
		if (error) {
			toast.error(Array.isArray(error) ? error[0].message : error);
		}
		setState((prevState) => ({
			...prevState,
			countryList: data,
			isLoading: false,
		}));
	};

	useEffect(() => {
		fetchCountryList();
	}, []);

	const fetchNotifications = async (
		pageSize = 5,
		pageNumber = 1,
		order = null,
		orderBy = "",
		filters = []
	) => {
		setState((prevState) => ({
			...prevState,
			isLoading: true,
		}));
		const group = {
			field: "notification",
			type: "=",
			value: true,
		};

		let filtersArr = [
			...filters.map((filter) => ({
				...filter,
				value: filter?.value?.value || filter?.value,
			})),
		];

		filtersArr = filtersArr.map((item) => {
			if (item.field === 'external_phone_number') {
				item.value = encodeURIComponent(item.value);
				item.alias = ["extension_number", "voicemail_extension"];
			}
			return item;
		});

		let queryString = queryStringBuilder(
			pageSize,
			(pageNumber - 1) * pageSize,
			[],
			filtersArr,
			{ field: orderBy, order: order }
		);
		// let count = 0;
		// filtersArr.forEach((item) => {
		// 	if (item.field === 'external_phone_number') {
		// 		item.value = encodeURIComponent(item.value);
		// 		queryString += `&filter[where][and][${count}][or][${count + 1}][extension_number][${item.type}]=${item.value}&filter[where][and][${count}][or][${count + 2}][voicemail_extension][${item.type}]=${item.value}`;
		// 		count++;
		// 	}
		// });

		const { data, error } = await Service.get(queryString);
		if (error) {
			return toast.error(Array.isArray(error) ? error[0]?.message : error);
		} else {
			gridHelpers && gridHelpers.resetSelection();
			setState((prevState) => ({
				...prevState,
				entries: data.notifications,
				isLoading: false,
				toggleAll: data.is_toggle_all_on,
				totalEntries: data.count || defaultState.totalEntries,
				selectedRows: [],
			}));
			return data;
		}
		// eslint-disable-next-line
	};

	useEffect(() => {
		if (state.needsAcknowledgement === null || state.needsAcknowledgement) {
			return;
		}
		fetchNotifications(
			state.pageSize,
			state.pageNumber,
			state.order,
			state.orderBy,
			state.filters
		);
	}, [
		state.pageSize,
		state.pageNumber,
		state.order,
		state.orderBy,
		state.filters,
		state.needsAcknowledgement,
	]);
	/**s
	 * Fetch latest value of toggle all switch.
	 */
	const fetchToggleAll = useCallback(async () => {
		let isFetchedSuccessfully = true;
		const { data, error } = await Service.get(
			queryStringBuilder(1, 1, [], [], null, false)
		);
		if (error) {
			isFetchedSuccessfully = false;
		} else {
			setState((prevState) => ({
				...prevState,
				rowBeingToggled: null,
				toggleAll: data.is_toggle_all_on,
			}));
		}

		return isFetchedSuccessfully;
	}, []);

	const fetchUserPreferences = useCallback(async () => {
		const { data, error } = await AppService.getUserDetails();
		if (error) {
			toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
		} else {
			setState((prevState) => ({
				...prevState,
				needsAcknowledgement: !data.user.regulations_911_acknowledgement,
				preference: {
					delete: data.user.user_preference.delete_notification,
					duplicate: data.user.user_preference.duplicate_notification,
					duplicateSuccess: data.user.user_preference.notification_duplicated,
					bulkEdit: data.user.user_preference.confirm_bulk_edit,
					notificationEdit: data.user.user_preference.notification_edited,
					toggle: data.user.user_preference.toggle_notification,
				},
			}));
		}
	}, []);

	useEffect(() => {
		fetchUserPreferences();
		return () => {
			setState(defaultState);
		};
	}, []);

	useEffect(() => {
		if (state.needsAcknowledgement === null || state.needsAcknowledgement) {
			return;
		}

		fetchNotifications();
	}, [state.needsAcknowledgement]);

	// Handlers

	/**
	 * Delete selected records.
	 */
	const handleDeleteConfirmation = useCallback(async () => {
			toast.info("Deleting notifications.", {
				autoClose: 2000,
			});
			setState((prevState) => {
				const updatedEntries = getDisabledRows(prevState);
				return {
					...prevState,
					entries: updatedEntries,
					isPaginationEnabled: false,
					isLoading: true,
					isDeleting: true,
					dialog: {
						...prevState.dialog,
						delete: false,
					},
				};
			});

			const query = state.areAllSelected
				? "?ids=ALL"
				// : state.cardView
				// ? `?ids=${state.selectedRows.map((row) => row.id).join(",")}`
				: `?ids=${state.selectedRows}`;
			const { error } = await Service.deleteNotification(query);
			if (error) {
				toast.error(
					Array.isArray(error) ? error[0] && error[0].message : error
				);
				setState((prevState) => ({
					...prevState,
					isDeleting: false,
				}));
			} else {
				toast.success("Deleted successfully");
				const notifications = await fetchNotifications();
				updatePaginationOnDelete(
					notifications && notifications.count,
					{
						pageSize: state.pageSize,
						pageNumber: state.pageNumber,
					},
					(updatedPageNumber) =>
						setState((prevState) => ({
							...prevState,
							pageNumber: updatedPageNumber,
						}))
				);

				if (gridHelpers) {
					gridHelpers.resetSelection();
					gridHelpers.resetAllSelection();
				}
				fetchNotifications(
					state.pageSize,
					state.pageNumber,
					state.order,
					state.orderBy
				);
				setState((prevState) => ({
					...prevState,
					selectedRows: [],
					areAllSelected: false,
					isDeleting: false,
				}));
			}
		},
		[fetchNotifications, state.areAllSelected, state.selectedRows]
	);

	/**
	 * Duplicate selected records
	 */
	const handleDuplicateConfirmation = async () => {
		toast.info("Duplicating notification(s).", {
			autoClose: 2000,
		});

		const timeout = setTimeout(() => {
			collapseToast.info("It is taking longer than expected, please wait.", {
				autoClose: 5000,
				transition: Zoom,
			});
		}, 6000);

		let latestState = { ...state };
		setState((prevState) => {
			const updatedEntries = getDisabledRows(
				prevState,
				state.rowsBeingDuplicated.length > 0
					? "rowsBeingDuplicated"
					: "selectedRows"
			);

			latestState = {
				...prevState,
				entries: updatedEntries,
				isDuplicating: true,
			};
			return {
				...prevState,
				entries: updatedEntries,
				isDuplicating: true,
				isLoading: true,
				pageNumber: 1,
			};
		});
		const payload =
			latestState.rowsBeingDuplicated.length > 0
				? latestState.rowsBeingDuplicated[0]
				// : state.cardView
				// ? latestState.selectedRows.map((row) => row.id)
				: latestState.areAllSelected ||
				  latestState.selectedRows;

		const query = `?ids=${payload}`;
		const { error } = await Service.duplicateNotification(query);
		clearTimeout(timeout);
		gridHelpers && gridHelpers.resetSelection();
		if (error) {
			toast.error(Array.isArray(error) ? error[0] && error[0].message : error);

			setState((prevState) => {
				const rowsBeingDuplicated =
					prevState.rowsBeingDuplicated.length > 0
						? prevState.rowsBeingDuplicated.slice(1)
						: [];

				return {
					...prevState,
					dialog: {
						...prevState.dialog,
						duplicate: false,
					},
					isDuplicating: false,
					rowsBeingDuplicated,
					selectedRows: [],
					isLoading: false,
				};
			});
		} else {
			setState((prevState) => {
				const rowsBeingDuplicated =
					prevState.rowsBeingDuplicated.length > 0
						? prevState.rowsBeingDuplicated.slice(1)
						: [];
				if (prevState.preference.duplicateSuccess) {
					toast.success("Duplicated successfully");
					return {
						...prevState,
						dialog: {
							...prevState.dialog,
							duplicate: false,
						},
						isDuplicating: false,
						rowsBeingDuplicated,
						selectedRows: [],
						isLoading: false,
					};
				} else {
					return {
						...prevState,
						dialog: {
							...prevState.dialog,
							duplicate: false,
							duplicateSuccess: true,
						},
						isDuplicating: false,
						rowsBeingDuplicated,
						selectedRows: [],
						isLoading: false,
					};
				}
			});
		}

		fetchNotifications(
			state.pageSize,
			state.pageNumber,
			state.order,
			state.orderBy,
			state.filters
		);
	};

	const handleDuplicateClick = () => {
		const debouncedConfirmationHandler = debounce(
			handleDuplicateConfirmation,
			400
		);
		setState((prevState) => {
			if (!prevState.preference.duplicate) {
				return {
					...prevState,
					dialog: {
						...prevState.dialog,
						duplicate: true,
					},
				};
			} else {
				debouncedConfirmationHandler();
				return prevState;
			}
		});
	};

	/**
	 * Reset filter state when filter is closed and fetch data.
	 */
	const handleFilterClose = useCallback(() => {
		setState((prevState) => ({
			...prevState,
			isFiltering: false,
			search: "",
			statusFilters: [],
			directionFilters: [],
		}));
	}, []);

	/**
	 * Update state when user selects any row from grid or card view.
	 */
	const handleSelectionChange = useCallback((selectedRows) => {
		setState((prevState) => ({ ...prevState, selectedRows }));
	}, []);

	const handleAllSelectionChange = useCallback((areAllSelected) => {
		setState((prevState) => ({ ...prevState, areAllSelected, selectedRows: [] }));
	}, []);

	/**
	 * Update state when user sorts data inside grid.
	 */
	// const handleSorting = useCallback((field, order) => {
	//   let sortObj = null;
	//   setState((prevState) => ({
	//     ...prevState,
	//     order,
	//     orderBy: order ? field.field || field.fieldName : null,
	//     selectedRows: defaultState.selectedRows,
	//   }));

	//   // if (order) {
	//   //   sortObj = { field, order };
	//   // }

	//   gridHelpers.resetSelection();
	//   // debounce(() => {
	//   //   setState((prevState) => ({ ...prevState, sort: sortObj }));
	//   // }, 500)();
	// }, []);

	const handleSorting = useCallback((fieldObj, order) => {
		setState((prevState) => ({
			...prevState,
			order,
			orderBy: order ? fieldObj.field || fieldObj.fieldName : null,
		}));
	}, []);

	/**
	 * Invoke the debounced function that is responsible for updating state
	 * when user is searching for something in filter.
	 */
	const handleSearchChange = useCallback(
		debounce(
			(search) =>
				setState((prevState) => ({ ...prevState, search, pageNumber: 1 })),
			1000
		),
		[]
	);

	const handleStatusConfirmation = useCallback(
		debounce(async (row = null) => {
			toast.info("Updating status.", { autoClose: 2000 });
			const rowBeingToggled = row || state.rowsBeingToggled[0];
			setState((prevState) => {
				const updatedRows = prevState.entries.map((row) =>
					row.id === rowBeingToggled.id
						? { ...row, enabled: !row.enabled, isDisabled: true }
						: row
				);

				return {
					...prevState,
					entries: updatedRows,
					dialog: {
						...prevState.dialog,
						toggle: {
							...prevState.dialog.toggle,
							rows: false,
						},
					},
				};
			});

			const { error } = await Service.toggle(
				`?ids=${rowBeingToggled.id}`,
				!rowBeingToggled.enabled
			);

			gridHelpers && gridHelpers.resetSelection();
			if (error) {
				toast.error(
					Array.isArray(error) ? error[0] && error[0].message : error
				);
			} else {
				const isFetchedSuccessfully = await fetchToggleAll();
				isFetchedSuccessfully && toast.success("Updated successfully.");
			}

			setState((prevState) => {
				const rowsBeingToggled =
					prevState.rowsBeingToggled.length > 0
						? prevState.rowsBeingToggled.slice(1)
						: [];
				return {
					...prevState,
					entries: prevState.entries.map((row) =>
						row.id === rowBeingToggled.id
							? error
								? { ...row, enabled: !row.enabled, isDisabled: false }
								: { ...row, isDisabled: false }
							: row
					),
					selectedRows: [],
					rowsBeingToggled,
				};
			});
		}, 600),
		[state.rowsBeingToggled, fetchToggleAll]
	);

	const handleStatusChange = useCallback(
		(row) => {
			setState((prevState) => {
				const rowsBeingToggled = prevState.rowsBeingToggled.slice();
				rowsBeingToggled.push({ ...row });
				if (!prevState.preference.toggle) {
					return {
						...prevState,
						rowsBeingToggled,
						dialog: {
							...prevState.dialog,
							toggle: {
								...prevState.dialog.toggle,
								rows: true,
							},
						},
					};
				} else {
					handleStatusConfirmation(row);
					return {
						...prevState,
						rowsBeingToggled,
					};
				}
			});
		},
		[handleStatusConfirmation]
	);

	const handleExport = debounce(async () => {
		const selectedNotificationIds = state.selectedRows
			.slice()
			.map((row) => row.id);
		debugger;
		const fileName = `Notifications_export_${
			selectedNotificationIds?.join("_") || "all"
		}`;
		toast.info("Exporting");
		let queryString = queryStringBuilder(
			0,
			0,
			[],
			[...state.filters, ...state.directionFilters],
			state.sort
		);
		if (!!state.selectedRows.length) {
			queryString += `${queryString ? "&" : "?"}ids=${state.selectedRows.join(
				","
			)}`;
		}
		const { data, error } = await Service.exportNotification(queryString);

		if (error) {
			toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
		} else {
			exportToFile(data, fileName);
		}
	}, 1000);

	const handlePageNumberChange = useCallback((pageNumber) => {
		setState((prevState) => ({
			...prevState,
			pageNumber,
		}));
	}, []);

	const handlePageSizeChange = useCallback((pageSize) => {
		setState((prevState) => ({
			...prevState,
			pageSize,
			pageNumber: 1,
		}));
	}, []);

	const handleToggleAllClick = () => {
		if (!state.preference.toggle) {
			setState((prevState) => ({
				...prevState,
				dialog: {
					toggle: {
						...prevState.dialog.toggle,
						all: true,
					},
				},
			}));
		} else {
			handleToggleAllConfirmation();
		}
	};

	const handleDuplicateClose = () => {
		setState((prevState) => ({
			...prevState,
			dialog: {
				...prevState.dialog,
				duplicateSuccess: false,
			},
		}));
	};

	const handleDuplicate = () => {
		setState((prevState) => ({
			...prevState,
			dialog: {
				...prevState.dialog,
				duplicateSuccess: false,
			},
		}));
	};

	const handleToggleAllConfirmation = debounce(async () => {
		setState((prevState) => ({
			...prevState,
			dialog: {
				...prevState.dialog,
				toggle: {
					...prevState.dialog.toggle,
					all: false,
				},
			},
			toggleAll: !prevState.toggleAll,
			entries: prevState.entries.map((entry) => ({
				...entry,
				isDisabled: true,
			})),
		}));

		toast.info("Updating status.", {
			autoClose: 2000,
		});

		const { error } = await Service.toggle("?ids=ALL", !state.toggleAll);

		gridHelpers && gridHelpers.resetSelection();
		gridHelpers && gridHelpers.resetAllSelection();
		if (error) {
			toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
		} else {
			toast.success("Updated successfully.");
			setState(prevState => ({
				...prevState,
				filters: [],
			}));
			filterHelper && filterHelper.clearFilter();
			return fetchNotifications();
		}

		setState((prevState) => ({
			...prevState,
			selectedRows: [],
			toggleAll: !!error ? !prevState.toggleAll : prevState.toggleAll,
			entries: prevState.entries.map((entry) => ({
				...entry,
				isDisabled: false,
			})),
		}));
	}, 600);

	const handleBulkEdit = () => {
		setState((prevState) => ({
			...prevState,
			dialog: {
				...prevState.dialog,
				bulkEdit: true,
			},
		}));
	};

	const handleDelete = () => {
		setState((prevState) => ({
			...prevState,
			dialog: {
				...prevState.dialog,
				delete: true,
			},
		}));
	};

	const handleCardView = () => {
		setState((prevState) => {
			setPageConfig(PAGE_KEYS.NOTIFICATIONS, {
				cardView: !prevState.cardView,
			});
			return {
				...prevState,
				cardView: !prevState.cardView,
			};
		});
	};

	const bulkEditOnClose = () => {
		setState((prevState) => ({
			...prevState,
			dialog: {
				...prevState.dialog,
				bulkEdit: false,
			},
		}));
	};

	const bulkEditOnPreferenceChange = (event) => {
		const checked = event.currentTarget.checked;
		setState((prevState) => {
			const updatedPreferences = {
				...prevState.preference,
				bulkEdit: !checked,
			};

			updatePreferences(updatedPreferences);
			return {
				...prevState,
				preference: updatedPreferences,
			};
		});
	};
	const bulkEditOnSave = () => {
		setState((prevState) => ({
			...prevState,
			dialog: {
				...prevState.dialog,
				bulkEdit: false,
			},
		}));
		fetchNotifications(
			state.pageSize,
			state.pageNumber,
			state.order,
			state.orderBy,
			state.filters
		);
	};

	const handleCancelToggle = () => {
		setState((prevState) => {
			const rowsBeingToggled =
				prevState.rowsBeingToggled.length > 0
					? prevState.rowsBeingToggled.slice(0, -1)
					: [];
			return {
				...prevState,
				dialog: {
					...prevState.dialog,
					toggle:
						prevState.rowsBeingToggled.length > 0
							? {
									...prevState.dialog.toggle,
									rows: false,
									all: prevState.dialog.toggle.all,
							  }
							: {
									...prevState.dialog.toggle,
									rows: prevState.dialog.toggle.rows,
									all: false,
							  },
				},
				rowsBeingToggled,
			};
		});
	};

	const handleToggleConfirmation = () => {
		if (state.rowsBeingToggled.length > 0) {
			handleStatusConfirmation(
				state.rowsBeingToggled[state.rowsBeingToggled.length - 1]
			);
		} else {
			handleToggleAllConfirmation();
		}
	};

	const handleFiltering = () => {
		setState((prevState) => ({
			...prevState,
			isFiltering: true,
		}));
	};

	const handleFilterChange = useCallback((filters = []) => {
		setState((prevState) => ({
			...prevState,
			filters,
			pageNumber: 1,
			areAllSelected: false,
			selectedRows: [],
		}));
	}, []);

	const handleClose = () => {
		setState((prevState) => ({
			...prevState,
			dialog: {
				...prevState.dialog,
				delete: false,
			},
		}));
	};
	// Data grid config
	const columns = [
		{
			id: "name",
			label: "Name",
			canSort: true,
			field: "name",
			render: (row) => (
				<Tooltip title={row.name} placement="top-start">
					<Typography
						sx={{ cursor: "pointer" }}
						onClick={() => {
							navigate(`/notifications/${row.id}/edit`);
						}}
						noWrap
						color="secondary"
					>
						{row.name}
					</Typography>
				</Tooltip>
			),
		},
		{
			id: "external_phone_number",
			label: "Number",
			canSort: true,
			field: "external_phone_number",
			hasEllipses: true,
			render: (row) => {
				// debugger;
				let content = "";

				if (row.is_extension_number) {
					content = row.extension_number;
				} else if (row.voicemail_extension) {
					content = row.voicemail_extension
				} else {
					content = row.external_phone_number || content;
				}
				return (
					<Typography
						sx={!row.enabled && { ...globalStyles.text.color.disabled }}
						variant="body2"
					>
						{content}
					</Typography>
				);
			},
		},
		{
			id: "call_direction",
			label: "Direction",
			canSort: true,
			field: "call_direction",
			render: (row) => (
				<Box sx={{ display: "flex", gap: "4px" }}>
					{row.call_direction === "Inbound" && (
						<img
							alt="inbound call direction icon"
							src={
								row.enabled
									? "/assets/inbound-call.svg"
									: "/assets/inbound-call-grey.svg"
							}
						/>
					)}
					{row.call_direction === "Outbound" && (
						<img
							alt="outbound call direction icon"
							src={
								row.enabled
									? "/assets/outbound-call.svg"
									: "/assets/outbound-call-grey.svg"
							}
						/>
					)}
					{row.call_direction === "Both Directions" && (
						<img
							alt="both direction call icon"
							src={
								row.enabled
									? "/assets/bothdirections-call.svg"
									: "/assets/bothdirections-call-grey.svg"
							}
						/>
					)}
					<Typography
						sx={!row.enabled && { ...globalStyles.text.color.disabled }}
						variant="body2"
					>
						{row.call_direction}
					</Typography>
				</Box>
			),
		},
		{
			label: "Delivery Method",
			canSort: false,
			render: (row) => {
				return (
					<Box sx={{ display: "flex", gap: "4px" }}>
					{(!!row.emails || !!(row.delay_config && !!row.delay_config.find(elem => elem.type === DELIVERY_TYPE.EMAIL))) && (
							<Tag
								// className={clsx("mr-1", style.emailTag, {
								//   [style.tagDisabled]: !row.enabled,
								// })}
								icon={<img src="/assets/email-white.svg" alt="" />}
								label="Email"
								color={!row.enabled ? "grey.400" : "#ffc617"}
							/>
						)}
						{(!!row.webhook || !!(row.delay_config && !!row.delay_config.find(elem => elem.type === DELIVERY_TYPE.WEBHOOK))) && (
							<Tag
								// className={clsx("mr-1", style.webhookTag, {
								//   [style.tagDisabled]: !row.enabled,
								// })
								icon={<img src="/assets/webhook-white.svg" alt="" />}
								label="Webhook"
								color={!row.enabled ? "grey.400" : "#ffa30a"}
							/>
						)}
						{(!!row.sms_to || !!(row.delay_config && !!row.delay_config.find(elem => elem.type === DELIVERY_TYPE.SMS)) ) && (
							<Tag
								className={clsx("mr-1", style.smsTag, {
									[style.tagDisabled]: !row.enabled,
								})}
								icon={<img src="/assets/phone-white.svg" alt="" />}
								label="SMS"
								color={!row.enabled ? "grey.400" : "#ffa30a"}
							/>
						)}
						{(!!row.glip_teams || !!(row.delay_config && !!row.delay_config.find(elem => elem.type === DELIVERY_TYPE.GLIP))) && (
							<Tag
								className={clsx(style.glipTag, {
									[style.tagDisabled]: !row.enabled,
								})}
								icon={<img src="/assets/glip.svg" alt="" />}
								label="Glip"
								color={!row.enabled ? "grey.400" : "#f50"}
							/>
						)}
						{(!!row.ringout_to_number || !!(row.delay_config && !!row.delay_config.find(elem => elem.type === DELIVERY_TYPE.RINGOUT))) && (
							<Tag
								className={clsx(style.glipTag, {
									[style.tagDisabled]: !row.enabled,
								})}
								icon={<img src="/assets/outbound-call-white.svg" alt="" />}
								label="Ringout"
								color={!row.enabled ? "grey.400" : "#f50"}
							/>
						)}
					</Box>
				);
			},
		},
		{
			id: "created_at",
			label: "Created",
			canSort: true,
			field: "created_at",
			render: (row) => (
				<Typography
					sx={!row.enabled && { ...globalStyles.text.color.disabled }}
					variant="body2"
				>
					{getFormattedDate(new Date(row.created_at))}
				</Typography>
			),
		},
		{
			id: "enabled",
			label: "Status",
			canSort: true,
			className: style.statusColumn,
			field: "enabled",
			headerClassName: style.statusColumn,
			render: (row, rowIndex) => {
				return (
					<FormControlLabel
						control={
							<Switch
								checked={row.enabled}
								sx={{ m: 1 }}
								disabled={!!row.isDisabled}
								labelClassName={clsx("p-relative", style.switchLabel)}
								onChange={() => {
									handleStatusChange(row, rowIndex);
								}}
							/>
						}
						sx={
							!row.enabled
								? {
										"& .MuiFormControlLabel-label": {
											...globalStyles.text.color.disabled,
										},
								  }
								: {
										"& .MuiFormControlLabel-label": {
											color: "#6258de",
											fontWeight: "bold",
										},
								  }
						}
						label={row.enabled ? "ON" : "OFF"}
					/>
				);
			},
		},
		{
			render: (row) => {
				return (
					<Dropdown
						customToggle={
							<img
								style={{
									display: "flex",
									cursor: "pointer",
									marginLeft: "40px",
								}}
								src="/assets/more-action.svg"
								alt=""
							/>
						}
						// onChange={(selectedOption) => {
						//   if (selectedOption.actionType === "Edit") {
						//     navigate(`/notifications/${row.id}/edit`);
						//   } else if (selectedOption.actionType === "Duplicate") {
						//     setState((prevState) => {
						//       const rowsBeingDuplicated =
						//         prevState.rowsBeingDuplicated.slice();
						//       rowsBeingDuplicated.push(row.id);
						//       return {
						//         ...prevState,
						//         rowsBeingDuplicated,
						//       };
						//     });
						//     setTimeout(handleDuplicateClick, 400);
						//   }
						// }}
						options={[
							{
								label: (
									<Box>
										<img
											alt="Edit"
											src="/assets/edit-default.svg"
											style={{ marginRight: "8px" }}
										/>
										Edit
									</Box>
								),
								onClick: () => {
									navigate(`/notifications/${row.id}/edit`);
								},
							},
							{
								label: (
									<Box>
										<img
											alt="Duplicate"
											src="/assets/duplicate-default.svg"
											style={{ marginRight: "8px" }}
										/>
										Duplicate
									</Box>
								),
								onClick: () => {
									setState((prevState) => {
										const rowsBeingDuplicated =
											prevState.rowsBeingDuplicated.slice();
										rowsBeingDuplicated.push(row.id);
										return {
											...prevState,
											rowsBeingDuplicated,
										};
									});
									setTimeout(handleDuplicateClick, 400);
								},
							},
						]}
						placement="bottom-end"
					/>
				);
			},
		},
	];
	//eslint-disable-next-line

	const filters = [
		{
			field: "name",
			fieldToDisplay: "Name",
			operatorType: "string",
		},
		{
			field: "external_phone_number",
			fieldToDisplay: "Number",
			operatorType: "string",
		},
		{
			field: "call_direction",
			fieldToDisplay: "Direction",
			operatorType: "array",
			options: [
				{ label: "Inbound", value: "Inbound" },
				{ label: "Outbound", value: "Outbound" },
				{ label: "Both Directions", value: "Both Directions" },
			],
		},
		{
			field: "enabled",
			fieldToDisplay: "Status",
			operatorType: "number",
			options: [
				{ label: "ON", value: "true" },
				{ label: "OFF", value: "false" },
			],
		},
	];

	const isDeleteDisabled =
		state.isLoading ||
		(!state.areAllSelected && state.selectedRows.length === 0);
	const disabled = state.totalEntries == 0 && !state.filters.length;
	if (state.needsAcknowledgement === null) {
		return <></>;
	}

	if (
		typeof state.needsAcknowledgement === "boolean" &&
		state.needsAcknowledgement
	) {
		return (
			<>
				<Dialog
					open={state.needsAcknowledgement}
					disableBackdropClick
					disableEscapeKeyDown
					sx={{
						// paper: style.acknowledgementDialog,
						minWidth: 534,
						minHeight: 468,
						// fontFamily: theme.font.regular,
						// color: theme.palette.grey[800],
						lineHeight: 1.4,
					}}
				>
					<DialogContent sx={{ minHeight: 468, minWidth: 534 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
							}}
						>
							<Typography variant="h6">911 Regulations</Typography>
						</Box>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								p: 3,
								height: "100%",
							}}
							className="d-flex flex-column f-align-between p-3 h-100"
						>
							<Typography variant="body1">
								<a
									href="https://support.ringcentral.com/s/article/911-Kari-Law-Support-RingCentral?language=en_US"
									target="_blank"
									rel="noopener noreferrer"
									className="color-secondary"
								>
									Kari’s Law
								</a>{" "}
								went into effect on February 2020 and we want to inform you of
								new 911 regulations. To proceed, please read and acknowledge
								this message.
							</Typography>
							<Typography sx={{ fontWeight: "bold" }} variant="body2">
								Minimum required info for a MLTS 911 call:
							</Typography>
							<Box>
								<Typography variant="body2" sx={{ mb: 2 }}>
									<b>(1)</b> the fact that a 911 call has been made,
								</Typography>
								<Typography variant="body2" sx={{ mb: 2 }}>
									<b>(2)</b> a valid callback number, and
								</Typography>
								<Typography variant="body2" sx={{ mb: 2 }}>
									<b>(3)</b> the information about the caller's location that
									the MLTS conveys to the public safety answering point (PSAP)
									with the call to 911
									<Box sx={{ ml: 4 }}>
										<Typography variant="body2" sx={{ mb: 2 }}>
											<b>(i)</b> MLTS notification must be initiated
											contemporaneously with the 911 call, provided that it is
											technically feasible to do so;
										</Typography>
										<Typography variant="body2" sx={{ mb: 2 }}>
											<b>(ii)</b> MLTS notification must not delay the call to
											911; and
										</Typography>
										<Typography variant="body2" sx={{ mb: 2 }}>
											<b>(iii)</b> MLTS notification must be sent to a location
											where someone is likely to see or hear it.
										</Typography>
									</Box>
								</Typography>
							</Box>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={async () => {
								toast.info("Updating, please wait.", {
									autoClose: 2000,
								});
								const { error } = await Service.acknowledgeRegulation();
								if (error) {
									toast.error(
										Array.isArray(error)
											? (error[0] && error[0].message) || DEFAULT_ERROR_MESSAGE
											: error
									);
								} else {
									toast.success("Updated successfully.");
									setState((prevState) => ({
										...prevState,
										needsAcknowledgement: false,
									}));
								}
							}}
							variant="contained"
							color="error"
						>
							Acknowledge
						</Button>
					</DialogActions>
				</Dialog>
			</>
		);
	}
	return (
		<>
			<NotificationView
				handleDuplicateClick={handleDuplicateClick}
				isLoading={state.isLoading}
				isDuplicating={state.isDuplicating}
				isDeleting={state.isDeleting}
				areAllSelected={state.areAllSelected}
				selectedRows={state.selectedRows}
				preference={state.preference}
				handleDeleteConfirmation={handleDeleteConfirmation}
				isDeleteDisabled={isDeleteDisabled}
				cardView={state.cardView}
				isFiltering={state.isFiltering}
				handleExport={handleExport}
				columns={columns}
				entries={state.entries}
				filters={filters}
				handleFilterClose={handleFilterClose}
				onGridReady={onGridReady}
				handlePageNumberChange={handlePageNumberChange}
				handlePageSizeChange={handlePageSizeChange}
				handleSelectionChange={handleSelectionChange}
				handleAllSelectionChange={handleAllSelectionChange}
				handleSorting={handleSorting}
				pageNumber={state.pageNumber}
				pageSize={state.pageSize}
				totalEntries={state.totalEntries}
				handleBulkEdit={handleBulkEdit}
				handleDelete={handleDelete}
				handleCardView={handleCardView}
				handleFiltering={handleFiltering}
				dialog={state.dialog}
				rowsBeingDuplicated={state.rowsBeingDuplicated}
				fetchCountryList={fetchCountryList}
				handleCancelDuplicate={handleCancelDuplicate}
				handleDuplicateConfirmation={handleDuplicateConfirmation}
				toggleAll={state.toggleAll}
				handleToggleAllClick={handleToggleAllClick}
				rowsBeingToggled={state.rowsBeingToggled}
				handleCancelToggle={handleCancelToggle}
				handleToggleConfirmation={handleToggleConfirmation}
				handleStatusChange={handleStatusChange}
				handleFilterChange={handleFilterChange}
				bulkEditOnClose={bulkEditOnClose}
				bulkEditOnPreferenceChange={bulkEditOnPreferenceChange}
				bulkEditOnSave={bulkEditOnSave}
				handleSuccess={() =>
					fetchNotifications(
						state.pageSize,
						state.pageNumber,
						state.order,
						state.orderBy,
						state.filters
					)
				}
				countryList={state.countryList}
				handleDuplicated={handleDuplicated}
				handleClose={handleClose}
				handleDuplicateClose={handleDuplicateClose}
				handleDuplicate={handleDuplicate}
				onPreferenceChange={onPreferenceChange}
				order={state.order}
				orderBy={state.orderBy}
				disabled={disabled}
				appliedFilters={state.filters}
				onFilterReady={helper => filterHelper = helper}
				loadedExtension={appData.loadedExtension}
			/>
		</>
	);
};

export default Notifications;
